import React from 'react';
import { Button } from './ui/button';
import { X, Sparkles, CheckCircle, Zap, TrendingUp, Users, FileText, GitBranch } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { auth } from '../utils/auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaywallModal = ({ feature, onClose, onUpgraded }) => {
  const navigate = useNavigate();
  const user = auth.getUser();
  const [upgrading, setUpgrading] = React.useState(false);

  const featureNames = {
    phases: 'Fase-system',
    time_tracking: 'Timeføring',
    tasks: 'Oppgaver',
    documents: 'Dokumenter & AI',
    crm: 'CRM',
    complex_cases: 'Komplekse saker'
  };

  const handleUpgrade = async () => {
    setUpgrading(true);
    try {
      const originUrl = window.location.origin;
      
      const response = await axios.post(`${API}/payments/checkout/session`, {
        package_id: 'lexy_pro',
        origin_url: originUrl,
        metadata: {
          user_id: user.id,
          email: user.email,
          feature: feature
        }
      });
      
      // Redirect to Stripe Checkout
      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Upgrade error:', error);
      toast.error('Kunne ikke starte betaling. Prøv igjen.');
      setUpgrading(false);
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm z-50" onClick={onClose} />
      
      {/* Modal */}
      <div className="fixed top-1/2 left-1/2 -trangray-x-1/2 -trangray-y-1/2 w-full max-w-2xl z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl border-2 border-gray-200 overflow-hidden">
          
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-600 to-blue-600 p-8 text-white relative">
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            
            <div className="flex items-center gap-4 mb-4">
              <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-white/30">
                <Sparkles className="w-8 h-8" />
              </div>
              <div>
                <h2 className="text-3xl font-bold mb-1">Oppgrader til Lexy Pro</h2>
                <p className="text-purple-100">
                  Lås opp {featureNames[feature] || 'denne funksjonen'}
                </p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-8">
            
            {/* Pricing */}
            <div className="text-center mb-8 pb-8 border-b border-gray-200">
              <div className="flex items-baseline justify-center gap-3 mb-2">
                <span className="text-6xl font-bold text-gray-900">999 kr</span>
                <span className="text-xl text-gray-600">/måned</span>
              </div>
              <p className="text-gray-600">
                <span className="font-bold text-emerald-600">14 dagers gratis prøveperiode</span> • Ingen binding
              </p>
            </div>

            {/* Features */}
            <div className="space-y-4 mb-8">
              <p className="text-sm font-bold text-gray-900 mb-4">Alt i Free, pluss:</p>
              {[
                { icon: GitBranch, text: 'AI-genererte saksfaser', desc: 'Strukturer komplekse saker automatisk' },
                { icon: FileText, text: 'Komplett dokumenthåndtering', desc: 'Grid Review, Drafting, Simulation' },
                { icon: Users, text: 'Fullt CRM-system', desc: 'Bedrifter, kontaktpersoner, integrasjoner' },
                { icon: Zap, text: 'AI Time & Task Suggestions', desc: 'Spar timer hver dag' },
                { icon: TrendingUp, text: 'Analytics & Rapporter', desc: 'Innsikt i din praksis' },
                { icon: CheckCircle, text: 'Ubegrensede komplekse saker', desc: 'Ingen begrensninger' }
              ].map((feature, i) => (
                <div key={i} className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <feature.icon className="w-5 h-5 text-purple-600" />
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold text-gray-900 mb-1">{feature.text}</div>
                    <div className="text-sm text-gray-600">{feature.desc}</div>
                  </div>
                  <CheckCircle className="w-5 h-5 text-emerald-600" />
                </div>
              ))}
            </div>

            {/* CTA */}
            <div className="space-y-3">
              <Button
                onClick={handleUpgrade}
                disabled={upgrading}
                size="lg"
                className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 py-6 text-lg"
              >
                {upgrading ? (
                  'Oppgraderer...'
                ) : (
                  <>
                    <Sparkles className="w-5 h-5 mr-2" />
                    Start 14 dagers gratis prøveperiode
                  </>
                )}
              </Button>
              
              <p className="text-center text-sm text-gray-500">
                Ingen kredittkort nødvendig • Avslutt når som helst
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PaywallModal;
